/** Site photos — Jobs group (canonical `H6IHDfRP`), the staff/photos/* family.
 *  Bytes never pass through here: upload-init hands back presigned R2 PUT URLs
 *  that the browser uses directly (see lib/photos.ts putWithProgress), and
 *  thumbnails arrive pre-signed on the list. Goes through the shared client. */

import { api } from "./client";
import type {
  DownloadUrl,
  PhotoSet,
  PhotoSetCreateInput,
  PhotoSetUpdateInput,
  PhotoUpdateInput,
  PhotoUploadFileDescriptor,
  PhotoUploadInitResponse,
  PhotosResponse,
  SitePhotoRow,
} from "@/types";

/** GET /staff/photos?job_id — the tab's one read: sets with counts + every
 *  live photo with a 1 h presigned thumbnail URL, taken_at ascending. */
export function listPhotos(jobId: number, signal?: AbortSignal): Promise<PhotosResponse> {
  return api.get<PhotosResponse>("jobs", "staff/photos", { query: { job_id: jobId }, signal });
}

/** GET /staff/photos/sets?job_id — sets with counts only (no presigning); the
 *  Job Detail tab label reads this. */
export function listPhotoSets(jobId: number, signal?: AbortSignal): Promise<PhotoSet[]> {
  return api.get<PhotoSet[]>("jobs", "staff/photos/sets", { query: { job_id: jobId }, signal });
}

/** POST /staff/photos/sets — a hand-made set (phase). */
export function createPhotoSet(input: PhotoSetCreateInput): Promise<PhotoSet> {
  return api.post<PhotoSet>("jobs", "staff/photos/sets", { body: input });
}

/** PATCH /staff/photos/sets/{id} — rename / redate / re-link. */
export function updatePhotoSet(setId: number, input: PhotoSetUpdateInput): Promise<PhotoSet> {
  return api.patch<PhotoSet>("jobs", `staff/photos/sets/${setId}`, { body: input });
}

/** DELETE /staff/photos/sets/{id} — empty sets only (400 otherwise). */
export function deletePhotoSet(setId: number): Promise<unknown> {
  return api.delete<unknown>("jobs", `staff/photos/sets/${setId}`);
}

/** POST /staff/photos/upload-init — pending rows + presigned PUT URLs for up
 *  to 25 files. Omit `photoSetId` for "auto by date taken" (one set per
 *  taken-day, created on the fly). */
export function initPhotoUpload(
  jobId: number,
  files: PhotoUploadFileDescriptor[],
  photoSetId?: number,
): Promise<PhotoUploadInitResponse> {
  const body: Record<string, unknown> = { job_id: jobId, files };
  if (photoSetId && photoSetId > 0) body.photo_set_id = photoSetId;
  return api.post<PhotoUploadInitResponse>("jobs", "staff/photos/upload-init", { body });
}

/** POST /staff/photos/confirm — flip pending rows live after both PUTs landed.
 *  Only the caller's own pending rows count; the response says how many. */
export function confirmPhotoUpload(items: { id: number; etag?: string }[]): Promise<{ confirmed: number }> {
  return api.post<{ confirmed: number }>("jobs", "staff/photos/confirm", { body: { items } });
}

/** GET /staff/photos/{id}/download-url — 300 s presigned URL for the ORIGINAL.
 *  Fetch at view time and use immediately. */
export function getPhotoDownloadUrl(photoId: number): Promise<DownloadUrl> {
  return api.get<DownloadUrl>("jobs", `staff/photos/${photoId}/download-url`);
}

/** POST /staff/photos/{id}/thumb-url — 15 min presigned PUT for the photo's
 *  thumbnail slot. The tab's self-heal for `has_thumb: false` photos: fetch
 *  the original, make the 512px JPEG, PUT it here, then confirmThumb. */
export function getThumbUploadUrl(photoId: number): Promise<DownloadUrl> {
  return api.post<DownloadUrl>("jobs", `staff/photos/${photoId}/thumb-url`, {});
}

/** POST /staff/photos/{id}/thumb-confirm — point the row at the thumbnail
 *  just PUT. From the next list read the tile is the thumbnail. */
export function confirmThumb(photoId: number): Promise<{ id: number; has_thumb: boolean }> {
  return api.post<{ id: number; has_thumb: boolean }>("jobs", `staff/photos/${photoId}/thumb-confirm`, {});
}

/** PATCH /staff/photos/{id} — caption / set / taken_at. */
export function updatePhoto(photoId: number, input: PhotoUpdateInput): Promise<SitePhotoRow> {
  return api.patch<SitePhotoRow>("jobs", `staff/photos/${photoId}`, { body: input });
}

/** POST /staff/photos/move — multi-select move into another set on the same job. */
export function movePhotos(ids: number[], photoSetId: number): Promise<{ moved: number; photo_set_id: number }> {
  return api.post<{ moved: number; photo_set_id: number }>("jobs", "staff/photos/move", {
    body: { ids, photo_set_id: photoSetId },
  });
}

/** DELETE /staff/photos/{id} — soft delete + both R2 objects (best effort).
 *  Also the uploader's cleanup for a pending row whose PUT failed. */
export function deletePhoto(photoId: number): Promise<unknown> {
  return api.delete<unknown>("jobs", `staff/photos/${photoId}`);
}
